import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useHowItWorksContent } from '../hooks/useCMSContent';
import { Search, Rocket, TrendingUp } from 'lucide-react';
import { EASING, useReducedMotion } from '../hooks/useMotion';

const defaultSteps = [
  { number: '01', title: 'Discover', timeline: 'Week 1', description: 'We learn your business, map workflows, and identify the highest-ROI AI opportunities.' },
  { number: '02', title: 'Build & Deploy', timeline: 'Weeks 2–4', description: 'We build, integrate, test, and launch your AI system — production-ready, not a prototype.' },
  { number: '03', title: 'Optimize', timeline: 'Ongoing', description: 'We monitor, tune, and report monthly so your AI keeps getting sharper.' }
];

const stepIcons = [Search, Rocket, TrendingUp];

const HowItWorksSection = () => {
  const sectionRef = useRef(null);
  const reduced = useReducedMotion();
  const { howItWorks } = useHowItWorksContent();

  const steps = howItWorks?.steps || defaultSteps;
  const badge = howItWorks?.badge || 'Process';
  const title = howItWorks?.title || 'From conversation to live system.';

  // Scroll-bound vertical line: scales Y from 0 → 1 as section scrolls through viewport
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start 70%', 'end 30%'],
  });
  const lineScale = useSpring(scrollYProgress, { stiffness: 90, damping: 30, mass: 0.4 });
  const lineGlow = useTransform(scrollYProgress, [0, 1], reduced ? [0.3, 0.3] : [0.3, 1]);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="relative py-24 bg-[#050505] overflow-hidden"
      data-testid="how-it-works-section"
    >
      <div className="orange-orb orange-orb-1 top-1/4 -left-32 w-96 h-96" />
      <div className="orange-orb orange-orb-2 bottom-1/4 -right-32 w-[28rem] h-[28rem]" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: EASING.appleSmooth }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5" data-testid="how-it-works-label">
            {badge}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h2>
        </motion.div>

        {/* Vertical timeline with scroll-bound draw */}
        <div className="relative">
          {/* Background track (dim) */}
          <div className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px h-full bg-white/[0.06]" aria-hidden="true" />
          {/* Scroll-drawn glowing line */}
          <motion.div
            style={{
              scaleY: lineScale,
              opacity: lineGlow,
              transformOrigin: 'top',
              willChange: 'transform, opacity',
            }}
            className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 w-px h-full bg-gradient-to-b from-[#FF6B00] via-[#FF6B00] to-[#FF6B00]/0 shadow-[0_0_18px_2px_rgba(255,107,0,0.5)]"
            aria-hidden="true"
          />

          <div className="space-y-12 md:space-y-20">
            {steps.map((step, index) => {
              const Icon = stepIcons[index] || TrendingUp;
              const isLeft = index % 2 === 0;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: 0.05, ease: EASING.cinematicSlow }}
                  className={`relative grid md:grid-cols-2 gap-6 md:gap-12 items-center ${isLeft ? '' : 'md:[&>*:first-child]:order-2'}`}
                  data-testid={`how-it-works-step-${index + 1}`}
                >
                  {/* Step content card */}
                  <div className={`${isLeft ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                      className="bg-[#0A0A0A] rounded-2xl p-6 border border-white/[0.08] hover:border-[#FF6B00]/30 transition-colors duration-500 card-glow-hover inline-block w-full"
                    >
                      <div className={`flex items-center gap-2.5 mb-4 ${isLeft ? 'md:justify-end' : 'md:justify-start'}`}>
                        <div className={`${isLeft ? 'md:order-2' : ''} w-11 h-11 rounded-xl bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-[#FF6B00]" />
                        </div>
                        <div className={isLeft ? 'md:order-1 md:text-right' : 'md:text-left'}>
                          <span className="block text-xs font-bold text-white tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>STEP {step.number}</span>
                          <span className="block text-[10px] text-[#71717A]">{step.timeline}</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed">{step.description}</p>
                    </motion.div>
                  </div>

                  {/* Center node — pulses when line reaches it */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true, amount: 0.6 }}
                      transition={{ duration: 0.5, delay: 0.2, ease: EASING.magneticSnap }}
                      className="relative"
                    >
                      <div className="w-4 h-4 rounded-full bg-[#FF6B00] shadow-[0_0_24px_4px_rgba(255,107,0,0.7)]" />
                      <motion.div
                        className="absolute inset-0 w-4 h-4 rounded-full bg-[#FF6B00]/40"
                        animate={{ scale: [1, 2.4, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                      />
                    </motion.div>
                  </div>

                  {/* Spacer on the other column */}
                  <div className="hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
